import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ChevronRight, ArrowLeft, User, Upload, X, Copy, Check, ExternalLink } from 'lucide-react';
import { API_URL, MEDIA_URL, CATEGORIES, ARCHIVE_SECTIONS, slugify, deslugify } from '../constants';

const SubcategoryPage = () => {
  const { categorySlug, subcategorySlug, personId } = useParams();
  const navigate = useNavigate();

  const categoryName = deslugify(categorySlug, CATEGORIES);
  const subcategories = CATEGORIES[categoryName] || {};
  const subcategoryName = typeof subcategories === 'object' && !Array.isArray(subcategories)
    ? deslugify(subcategorySlug, Object.fromEntries((subcategories || []).map(s => [s, s])))
    : (subcategories || []).find(s => slugify(s) === subcategorySlug) || subcategorySlug;

  const section = ARCHIVE_SECTIONS.find(s => slugify(s.category) === categorySlug);

  const [people, setPeople]                     = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [selectedPerson, setSelectedPerson]     = useState(null);
  const [showModal, setShowModal]               = useState(false);
  const [showSuggestionForm, setShowSuggestionForm] = useState(false);
  const [approvedSuggestions, setApprovedSuggestions] = useState([]);
  const [copied, setCopied]                     = useState(false);
  const [suggestionData, setSuggestionData]     = useState({
    contributorName: '', contributorEmail: '', contributorPhone: '',
    suggestionText: '', suggestedStatus: '', suggestedBirthDate: '',
    suggestedGender: '', suggestedProfession: '', suggestedAddress: '',
    supportingDocument: null,
  });
  const [submitMsg, setSubmitMsg] = useState('');

  // ── Load people ─────────────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/subcategories/`)
      .then(r => r.json())
      .then(d => {
        const arr = Array.isArray(d) ? d : (d.results || []);
        const matchSub = arr.find(s => slugify(s.name) === subcategorySlug);
        if (matchSub) return fetch(`${API_URL}/people/by_subcategory/?subcategory_id=${matchSub.id}`);
        return null;
      })
      .then(r => r ? r.json() : [])
      .then(d => {
        const arr = Array.isArray(d) ? d : (d.results || []);
        setPeople(arr.map((p, i) => ({
          no: i + 1,
          id: p.id,
          name: p.full_name || `${p.title ? p.title + ' ' : ''}${p.first_name} ${p.last_name}`.trim(),
          details: p.details,
          achievements: p.achievements,
          birth_date: p.birth_date,
          status: p.status,
          gender: p.gender,
          profession: p.profession,
          address: p.address,
          photo: p.photo,
          death_date: p.death_date,
        })));
      })
      .catch(() => setPeople([]))
      .finally(() => setLoading(false));
  }, [subcategorySlug]);

  // ── Deep-link: open person modal when personId is in the URL ─────────────
  useEffect(() => {
    if (!personId || people.length === 0) return;
    const target = people.find(p => String(p.id) === String(personId));
    if (target) openPerson(target, /* updateUrl = */ false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId, people]);

  // ── Build the shareable URL for the currently open person ────────────────
  const buildPersonUrl = (person) => {
    const base = window.location.origin;
    return `${base}/archive/${categorySlug}/${subcategorySlug}/person/${person.id}`;
  };

  // ── Open modal + update browser URL ──────────────────────────────────────
  const openPerson = useCallback(async (person, updateUrl = true) => {
    setSelectedPerson(person);
    setShowModal(true);
    setCopied(false);

    if (updateUrl) {
      navigate(
        `/archive/${categorySlug}/${subcategorySlug}/person/${person.id}`,
        { replace: false }   // push onto history so Back button works
      );
    }

    // Fetch approved community suggestions
    try {
      const r = await fetch(`${API_URL}/person-suggestions/by_person/?person_id=${person.id}`);
      const d = await r.json();
      setApprovedSuggestions(
        (Array.isArray(d) ? d : (d.results || [])).filter(
          s => ['approved', 'integrated'].includes(s.status)
        )
      );
    } catch (_err) {
      setApprovedSuggestions([]);
    }
  }, [categorySlug, subcategorySlug, navigate]);

  // ── Close modal + restore list URL ────────────────────────────────────────
  const closeModal = () => {
    setShowModal(false);
    setApprovedSuggestions([]);
    setCopied(false);
    navigate(`/archive/${categorySlug}/${subcategorySlug}`, { replace: true });
  };

  // ── Copy Link to clipboard ────────────────────────────────────────────────
  const copyLink = async () => {
    if (!selectedPerson) return;
    const url = buildPersonUrl(selectedPerson);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_err) {
      // Fallback for browsers that block clipboard API
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  // ── Suggestion submit ────────────────────────────────────────────────────
  const handleSuggestionSubmit = async () => {
    if (!suggestionData.contributorName || !suggestionData.suggestionText) {
      setSubmitMsg('Please fill in your name and suggestion.'); return;
    }
    const fd = new FormData();
    fd.append('person', selectedPerson.id);
    const keyMap = {
      contributorName: 'contributor_name', contributorEmail: 'contributor_email',
      contributorPhone: 'contributor_phone', suggestionText: 'suggestion_text',
      suggestedStatus: 'suggested_status', suggestedBirthDate: 'suggested_birth_date',
      suggestedGender: 'suggested_gender', suggestedProfession: 'suggested_profession',
      suggestedAddress: 'suggested_address', supportingDocument: 'supporting_document',
    };
    Object.entries(suggestionData).forEach(([k, v]) => { if (v) fd.append(keyMap[k] || k, v); });
    try {
      await fetch(`${API_URL}/person-suggestions/`, { method: 'POST', body: fd });
      setSubmitMsg('✅ Suggestion submitted! Thank you for contributing.');
      setShowSuggestionForm(false);
      setSuggestionData({
        contributorName: '', contributorEmail: '', contributorPhone: '',
        suggestionText: '', suggestedStatus: '', suggestedBirthDate: '',
        suggestedGender: '', suggestedProfession: '', suggestedAddress: '',
        supportingDocument: null,
      });
    } catch (_err) {
      setSubmitMsg('Failed to submit. Please try again.');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-gray-500 mb-6 flex-wrap">
        <Link to="/" className="hover:text-emerald-700">Home</Link><ChevronRight className="w-3.5 h-3.5" />
        <Link to="/archive" className="hover:text-emerald-700">Archive</Link><ChevronRight className="w-3.5 h-3.5" />
        <Link to={`/archive/${categorySlug}`} className="hover:text-emerald-700">{categoryName}</Link><ChevronRight className="w-3.5 h-3.5" />
        <span className="text-gray-800 font-medium">{subcategoryName}</span>
      </nav>

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-6">
        <div className={`bg-gradient-to-r ${section ? 'from-emerald-700 to-emerald-900' : 'from-gray-700 to-gray-900'} p-6 text-white`}>
          <button onClick={() => navigate(`/archive/${categorySlug}`)} className="flex items-center gap-1.5 text-white/80 hover:text-white mb-3 text-sm transition">
            <ArrowLeft className="w-4 h-4" /> Back to {categoryName}
          </button>
          <div className="text-3xl mb-2">{section?.icon || '📁'}</div>
          <h1 className="text-2xl font-bold">{subcategoryName}</h1>
          <p className="text-white/80 text-sm mt-1">{categoryName}</p>
        </div>
      </div>

      {/* People Table */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800">Entries</h2>
          <span className="text-xs text-gray-400 italic">Click "View Profile" to see full details & share link</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['#', 'Name', 'Profile', 'Contribute'].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="4" className="px-6 py-10 text-center">
                  <div className="flex items-center justify-center gap-3 text-gray-500">
                    <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                    Loading entries…
                  </div>
                </td></tr>
              ) : people.length > 0 ? people.map(p => (
                <tr key={p.id} className="hover:bg-gray-50 transition">
                  <td className="px-6 py-4 text-sm text-gray-500">{p.no}</td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{p.name}</td>
                  <td className="px-6 py-4 text-sm">
                    <button
                      onClick={() => openPerson(p)}
                      className="text-emerald-700 hover:text-emerald-900 hover:underline font-medium flex items-center gap-1"
                    >
                      View Profile <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <button
                      onClick={() => { setSelectedPerson(p); setShowSuggestionForm(true); }}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-xs font-medium"
                    >
                      + Contribute
                    </button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="4" className="px-6 py-12 text-center">
                  <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="font-medium text-gray-600">No data available yet for this subcategory.</p>
                  <p className="text-sm text-gray-400 mt-1">Be the first to contribute!</p>
                  <Link to="/contribute/archive" className="mt-4 inline-block px-5 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition text-sm">Contribute Data</Link>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {people.length > 0 && (
          <div className="px-6 py-3 bg-gray-50 border-t text-sm text-gray-500">
            {people.length} entr{people.length === 1 ? 'y' : 'ies'}
          </div>
        )}
      </div>

      {/* ── Person Profile Modal ──────────────────────────────────────────── */}
      {showModal && selectedPerson && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
              <h3 className="text-xl font-bold text-gray-900 truncate pr-4">{selectedPerson.name}</h3>
              <button onClick={closeModal} className="flex-shrink-0 p-1 rounded-full hover:bg-gray-100 transition">
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* ── Shareable URL banner ─────────────────────────────────── */}
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-400 mb-0.5 font-medium uppercase tracking-wide">Profile Link</p>
                  <p className="text-xs text-gray-600 font-mono truncate">{buildPersonUrl(selectedPerson)}</p>
                </div>
                <button
                  onClick={copyLink}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                    copied
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-800 text-white hover:bg-gray-700'
                  }`}
                >
                  {copied ? (
                    <><Check className="w-3.5 h-3.5" /> Copied!</>
                  ) : (
                    <><Copy className="w-3.5 h-3.5" /> Copy Link</>
                  )}
                </button>
              </div>

              {/* Photo */}
              <div className="flex justify-center">
                {selectedPerson.photo ? (
                  <img
                    src={selectedPerson.photo.startsWith('http') ? selectedPerson.photo : `${MEDIA_URL}${selectedPerson.photo}`}
                    alt={selectedPerson.name}
                    className="w-40 h-40 object-cover rounded-xl shadow-md"
                  />
                ) : (
                  <div className="w-40 h-40 bg-gray-100 rounded-xl flex items-center justify-center">
                    <User className="w-20 h-20 text-gray-300" />
                  </div>
                )}
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['Status', selectedPerson.status],
                  ['Gender', selectedPerson.gender],
                  ['Profession', selectedPerson.profession],
                  ['Date of Birth', selectedPerson.birth_date ? new Date(selectedPerson.birth_date).toLocaleDateString() : null],
                  ['Address', selectedPerson.address],
                ]
                  .filter(([, v]) => v && v !== '-')
                  .map(([label, value]) => (
                    <div key={label} className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-gray-500 mb-1">{label}</p>
                      <p className="text-sm text-gray-800">{value}</p>
                    </div>
                  ))}
              </div>

              {/* Details */}
              {selectedPerson.details && (
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Details</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedPerson.details}</p>
                </div>
              )}

              {/* Achievements */}
              {selectedPerson.achievements && selectedPerson.achievements !== 'N/A' && (
                <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Achievements</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedPerson.achievements}</p>
                </div>
              )}

              {/* Community contributions */}
              {approvedSuggestions.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-bold text-green-800 mb-3">✅ Community Contributions</h4>
                  {approvedSuggestions.map((s, i) => (
                    <div key={i} className="bg-white border border-green-100 rounded-lg p-3 mb-2">
                      <p className="text-xs text-gray-500 mb-1">By {s.contributor_name} · {new Date(s.created_at).toLocaleDateString()}</p>
                      <p className="text-sm text-gray-700 italic">"{s.suggestion_text}"</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex gap-3 flex-wrap">
              <button
                onClick={copyLink}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  copied ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {copied ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy Link</>}
              </button>
              <button
                onClick={() => { setShowModal(false); setShowSuggestionForm(true); }}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition text-sm font-medium"
              >
                + Contribute Suggestion
              </button>
              <button
                onClick={closeModal}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Suggestion Form Modal ─────────────────────────────────────────── */}
      {showSuggestionForm && selectedPerson && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold">Contribute about {selectedPerson.name}</h3>
              <button onClick={() => setShowSuggestionForm(false)}><X className="w-6 h-6 text-gray-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                Help us improve this person's profile with corrections or additional info.
              </div>
              {submitMsg && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700">{submitMsg}</div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[['Your Name *', 'text', 'contributorName'], ['Your Email', 'email', 'contributorEmail'], ['Your Phone', 'tel', 'contributorPhone']].map(([label, type, key]) => (
                  <div key={key} className={key === 'contributorPhone' ? 'sm:col-span-2' : ''}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                    <input type={type} value={suggestionData[key]} onChange={e => setSuggestionData(p => ({ ...p, [key]: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
                  </div>
                ))}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Suggestion *</label>
                <textarea value={suggestionData.suggestionText} onChange={e => setSuggestionData(p => ({ ...p, suggestionText: e.target.value }))} rows="4" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" placeholder="Describe your suggestion or correction…" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[['Suggested Status', 'text', 'suggestedStatus'], ['Suggested Profession', 'text', 'suggestedProfession'], ['Suggested Birth Date', 'date', 'suggestedBirthDate']].map(([label, type, key]) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                    <input type={type} value={suggestionData[key]} onChange={e => setSuggestionData(p => ({ ...p, [key]: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" />
                  </div>
                ))}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Suggested Gender</label>
                  <select value={suggestionData.suggestedGender} onChange={e => setSuggestionData(p => ({ ...p, suggestedGender: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none">
                    <option value="">Select</option><option value="Male">Male</option><option value="Female">Female</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Supporting Document (optional)</label>
                <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 w-fit">
                  <Upload className="w-4 h-4" /> Upload File
                  <input type="file" accept="image/*,.pdf,.doc,.docx" className="hidden" onChange={e => setSuggestionData(p => ({ ...p, supportingDocument: e.target.files[0] }))} />
                </label>
                {suggestionData.supportingDocument && <p className="text-xs text-gray-500 mt-1">📎 {suggestionData.supportingDocument.name}</p>}
              </div>
            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex gap-3">
              <button onClick={handleSuggestionSubmit} className="flex-1 bg-emerald-700 text-white py-2 rounded-lg hover:bg-emerald-800 transition font-medium">Submit Suggestion</button>
              <button onClick={() => setShowSuggestionForm(false)} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubcategoryPage;
